import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const sampleLinks = [
  { title: "PREL International Trailer", href: "/media/PREL_International_Trailer.mp4" },
  { title: "PREL x WWE Edit", href: "/media/PREL_WWE_Edit.mp4" },
  { title: "PREL Media Kit 2025", href: "/media/PREL_MediaKit_2025.pdf" },
];

const copyByLang = {
  es: {
    title: "Prensa y Medios | Puerto Rico Esports League",
    description: "Información oficial de prensa y medios de Puerto Rico Esports League, incluyendo resumen, posicionamiento, materiales y contacto.",
    ogDescription: "Información oficial de prensa y medios para PREL.",
    eyebrow: "Prensa y Medios",
    heroTitle: "Información oficial de PREL para medios",
    heroBody:
      "Puerto Rico Esports League (PREL) es una plataforma de esports y entretenimiento construida desde Puerto Rico a través de televisión, contenido digital y eventos en vivo. Esta página funciona como punto de partida para medios, aliados estratégicos, auspiciadores y colaboradores externos que necesiten una explicación clara de la plataforma.",
    boilerplateEyebrow: "Boilerplate",
    boilerplate:
      "Puerto Rico Esports League es una plataforma de esports y entretenimiento basada en Puerto Rico que produce contenido original, eventos en vivo y programación televisiva. PREL opera en la intersección de la cultura competitiva, la televisión, la comunidad y la narrativa de marca, con la visión de crecer desde Puerto Rico hacia una propiedad mediática con relevancia regional e internacional.",
    pointsEyebrow: "Puntos clave de posicionamiento",
    pressContactEyebrow: "Contacto de prensa",
    downloadKit: "Descargar Media Kit",
    metricsEyebrow: "Resumen de métricas",
    metricsTitle: "Audiencia y contexto de desempeño",
    metricsNote:
      "El desempeño televisivo de PREL debe entenderse en su contexto local: el programa se transmite en un canal de televisión de Puerto Rico y ha alcanzado aproximadamente 10 puntos de rating por programa, un nivel significativo para un formato independiente de gaming y esports construido en la isla.",
    packageEyebrow: "Paquete sugerido para medios",
    packageTitle: "Materiales que pueden compartirse a solicitud",
    availableEyebrow: "Disponible ahora",
    pressPoints: [
      "Plataforma de esports y entretenimiento basada en Puerto Rico",
      "Contenido original, eventos en vivo y programación televisiva",
      "Formato ideal para marcas, medios y activaciones en televisión, digital y experiencias presenciales",
      "Construida para relevancia cultural en Puerto Rico con proyección hacia el mercado hispano en Estados Unidos y Latinoamérica",
    ],
    mediaAssets: [
      {
        title: "Resumen de marca y plataforma",
        desc: "Una vista concisa de la plataforma PREL, su posicionamiento y su potencial para medios, marcas y aliados estratégicos.",
      },
      {
        title: "Resumen de métricas",
        desc: "Audiencia, tracción digital, contexto de desempeño televisivo e indicadores de alcance útiles para conversaciones con marcas y medios.",
      },
      {
        title: "Muestras de contenido seleccionado",
        desc: "Materiales representativos del programa de PREL, su cobertura de eventos y su ecosistema de narrativa digital.",
      },
    ],
    metrics: [
      ["100K+", "Suscriptores y comunidad social"],
      ["20M+", "Impresiones digitales"],
      ["25%", "Interacción"],
      ["500K", "Alcance digital mensual"],
      ["~10", "Puntos de rating por programa"],
      ["48", "Episodios producidos"],
    ],
  },
  en: {
    title: "Press and Media | Puerto Rico Esports League",
    description: "Official Puerto Rico Esports League press and media information, including overview, positioning, materials, and contact details.",
    ogDescription: "Official press and media information for PREL.",
    eyebrow: "Press and Media",
    heroTitle: "Official PREL information for media",
    heroBody:
      "Puerto Rico Esports League (PREL) is an esports and entertainment platform built from Puerto Rico through television, digital content, and live events. This page serves as a starting point for media, strategic partners, sponsors, and external collaborators who need a clear explanation of the platform.",
    boilerplateEyebrow: "Boilerplate",
    boilerplate:
      "Puerto Rico Esports League is an esports and entertainment platform based in Puerto Rico that produces original content, live events, and television programming. PREL operates at the intersection of competitive culture, television, community, and brand storytelling, with a vision to grow from Puerto Rico into a media property with regional and international relevance.",
    pointsEyebrow: "Key positioning points",
    pressContactEyebrow: "Press contact",
    downloadKit: "Download Media Kit",
    metricsEyebrow: "Metrics summary",
    metricsTitle: "Audience and performance context",
    metricsNote:
      "PREL's television performance should be understood in its local context: the show airs on a television channel in Puerto Rico and has reached roughly 10 rating points per episode, a meaningful level for an independent gaming and esports format built on the island.",
    packageEyebrow: "Suggested media package",
    packageTitle: "Materials that can be shared on request",
    availableEyebrow: "Available now",
    pressPoints: [
      "An esports and entertainment platform based in Puerto Rico",
      "Original content, live events, and television programming",
      "An ideal format for brands, media, and activations across TV, digital, and in-person experiences",
      "Built for cultural relevance in Puerto Rico with projection toward the U.S. Hispanic market and Latin America",
    ],
    mediaAssets: [
      {
        title: "Brand and platform overview",
        desc: "A concise view of the PREL platform, its positioning, and its potential for media, brands, and strategic partners.",
      },
      {
        title: "Metrics summary",
        desc: "Audience, digital traction, TV performance context, and reach indicators useful for conversations with brands and media.",
      },
      {
        title: "Selected content samples",
        desc: "Representative materials from PREL's show, its event coverage, and its digital storytelling ecosystem.",
      },
    ],
    metrics: [
      ["100K+", "Subscribers and social community"],
      ["20M+", "Digital impressions"],
      ["25%", "Engagement"],
      ["500K", "Monthly digital reach"],
      ["~10", "TV rating points per episode"],
      ["48", "Episodes produced"],
    ],
  },
};

export default function PressPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];

  useEffect(() => {
    document.title = copy.title;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.description);
    setMeta("og:title", copy.title, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/press", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/press");

    const schemaId = "prel-press-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          name: copy.title,
          url: "https://www.puertoricoesportleague.com/press",
          description: copy.description,
        },
        {
          "@type": "Organization",
          name: "Puerto Rico Esports League",
          alternateName: "PREL",
          url: "https://www.puertoricoesportleague.com",
          logo: "https://www.puertoricoesportleague.com/logo.png",
          email: "info@puertoricoesportleague.com",
          telephone: "+1-787-918-7979",
        },
      ],
    });
  }, [copy]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="/press" />

      <main className="max-w-[1300px] mx-auto px-6 xl:px-8 pt-12 pb-20">
        <p className="text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.eyebrow}</p>
        <h1 className="mt-4 text-[clamp(2.5rem,7vw,5.5rem)] font-black leading-[0.9] uppercase">{copy.heroTitle}</h1>
        <p className="mt-5 max-w-4xl text-slate-300 text-lg leading-relaxed">{copy.heroBody}</p>

        <section className="mt-12 border border-white/10 bg-[#111722] p-7 md:p-8">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">{copy.boilerplateEyebrow}</p>
          <p className="mt-4 text-slate-200 leading-relaxed text-lg">{copy.boilerplate}</p>
        </section>

        <section className="mt-10 grid lg:grid-cols-2 gap-6">
          <article className="border border-white/10 bg-[#111722] p-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.pointsEyebrow}</p>
            <ul className="mt-5 space-y-3 text-slate-300 leading-relaxed">
              {copy.pressPoints.map((point) => (
                <li key={point}>• {point}</li>
              ))}
            </ul>
          </article>

          <article className="border border-white/10 bg-[#111722] p-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.pressContactEyebrow}</p>
            <div className="mt-5 space-y-3 text-slate-300 leading-relaxed">
              <p><strong className="text-white">Puerto Rico Esports League</strong></p>
              <p>Email: <a href="mailto:info@puertoricoesportleague.com" className="text-cyan-300 hover:underline">info@puertoricoesportleague.com</a></p>
              <p>Website: <a href="https://www.puertoricoesportleague.com" className="text-cyan-300 hover:underline">www.puertoricoesportleague.com</a></p>
              <div className="pt-2">
                <a href="/media/PREL_MediaKit_2025.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex px-4 py-2 border border-cyan-300/40 text-cyan-300 hover:bg-cyan-400/10 transition">
                  {copy.downloadKit}
                </a>
              </div>
            </div>
          </article>
        </section>

        <section className="mt-10 border border-white/10 bg-[#111722] p-7 md:p-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.metricsEyebrow}</p>
          <h2 className="mt-3 text-3xl font-black uppercase">{copy.metricsTitle}</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
            {copy.metrics.map(([value, label]) => (
              <article key={label} className="border border-white/10 bg-[#0F1220] p-5">
                <p className="text-3xl font-black">{value}</p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mt-2">{label}</p>
              </article>
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-400 max-w-4xl leading-relaxed">{copy.metricsNote}</p>
        </section>

        <section className="mt-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.packageEyebrow}</p>
          <h2 className="mt-3 text-3xl font-black uppercase">{copy.packageTitle}</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {copy.mediaAssets.map((item) => (
              <article key={item.title} className="border border-white/10 bg-[#111722] p-6">
                <h3 className="text-xl font-bold">{item.title}</h3>
                <p className="mt-3 text-slate-300 leading-relaxed">{item.desc}</p>
              </article>
            ))}
          </div>

          <div className="mt-8 border border-white/10 bg-[#111722] p-6 md:p-7">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">{copy.availableEyebrow}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {sampleLinks.map((item) => (
                <a key={item.href} href={item.href} target="_blank" rel="noopener noreferrer" className="px-4 py-2 border border-white/15 text-white hover:bg-white/5 transition">
                  {item.title}
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
